import React, { useEffect, useState, useRef, useCallback } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap, useMapEvents } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Search, X, MapPin, Star, Heart, GraduationCap, Euro, ChevronRight, Share2, User } from 'lucide-react';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Fix for default markers
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

const typeColors = {
  service: '#D4A574',
  course: '#27AE60'
};

const typeLabels = {
  service: { es: 'Sesiones', en: 'Services' },
  course: { es: 'Cursos', en: 'Courses' }
};

// Apply offset to markers sharing same coordinates - distribute in circle
const applyCoordinateOffset = (markers) => {
  // Filter only markers with valid coordinates first
  const validMarkers = markers.filter(m => 
    m && 
    typeof m.latitude === 'number' && 
    typeof m.longitude === 'number' && 
    !isNaN(m.latitude) && 
    !isNaN(m.longitude) &&
    m.latitude !== 0 && 
    m.longitude !== 0
  );
  
  const coordMap = new Map();
  
  // Group markers by coordinates (round to 3 decimals ~100m precision for grouping nearby markers)
  validMarkers.forEach((marker, index) => {
    const key = `${marker.latitude.toFixed(3)},${marker.longitude.toFixed(3)}`;
    if (!coordMap.has(key)) {
      coordMap.set(key, []);
    }
    coordMap.get(key).push(index);
  });
  
  // Apply offset to ALL markers in groups with duplicates
  const result = [...validMarkers];
  coordMap.forEach((indices) => {
    if (indices.length > 1) {
      // Small offset: ~30m - keeps markers clustered together but separates them when expanded
      const offsetDistance = 0.0003;
      const count = indices.length;
      
      // Distribute ALL markers evenly in a circle
      indices.forEach((idx, i) => {
        const angle = (2 * Math.PI * i) / count;
        const newLat = result[idx].latitude + (offsetDistance * Math.cos(angle));
        const newLng = result[idx].longitude + (offsetDistance * Math.sin(angle));
        
        // Only apply if new coordinates are valid
        if (!isNaN(newLat) && !isNaN(newLng)) {
          result[idx] = {
            ...result[idx],
            latitude: newLat,
            longitude: newLng
          };
        }
      });
    }
  });
  
  return result;
};

// Component to fit bounds
function FitBounds({ markers }) {
  const map = useMap();
  
  useEffect(() => {
    if (!map || !map._container) return;
    try {
      if (markers.length > 0) {
        const validMarkers = markers.filter(m => m.latitude && m.longitude && m.latitude !== 0 && m.longitude !== 0);
        if (validMarkers.length > 0) {
          const bounds = L.latLngBounds(validMarkers.map(m => [m.latitude, m.longitude]));
          map.fitBounds(bounds, { padding: [50, 50], maxZoom: 11 });
        }
      }
    } catch (e) {
      console.warn('Map fitBounds error:', e);
    }
  }, [markers, map]);
  
  return null;
}

// Component to handle map click events
function MapClickHandler({ onMapClick }) {
  useMapEvents({
    click: (e) => {
      onMapClick(e);
    }
  });
  return null;
}

// Create photo marker with image thumbnail
const createPhotoMarker = (marker, isFeatured = false) => {
  const color = typeColors[marker.type] || '#D4A574';
  const hasImage = marker.image && marker.image.length > 0;
  const pulseClass = isFeatured ? 'marker-pulse' : '';
  
  return L.divIcon({
    className: `custom-photo-marker ${pulseClass}`,
    html: `
      <div class="marker-container" style="
        position: relative;
        width: 56px;
        height: 56px;
        cursor: pointer;
        transition: transform 0.2s ease;
      ">
        <div style="
          position: absolute;
          bottom: 0;
          left: 50%;
          transform: translateX(-50%);
          width: 0;
          height: 0;
          border-left: 10px solid transparent;
          border-right: 10px solid transparent;
          border-top: 12px solid ${color};
          filter: drop-shadow(0 2px 4px rgba(0,0,0,0.2));
        "></div>
        <div style="
          position: absolute;
          top: 0;
          left: 0;
          width: 56px;
          height: 44px;
          background: ${hasImage ? `url(${marker.image}) center/cover` : color};
          border-radius: 8px;
          border: 3px solid ${color};
          box-shadow: 0 4px 12px rgba(0,0,0,0.25);
          display: flex;
          align-items: center;
          justify-content: center;
          overflow: hidden;
        ">
          ${!hasImage ? `
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2">
              ${marker.type === 'service' ? '<path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z"></path>' : ''}
              ${marker.type === 'course' ? '<path d="M22 10v6M2 10l10-5 10 5-10 5z"></path><path d="M6 12v5c3 3 9 3 12 0v-5"></path>' : ''}
            </svg>
          ` : ''}
        </div>
        ${isFeatured ? `
          <div style="
            position: absolute;
            top: -6px;
            right: -6px;
            background: #FFD700;
            border-radius: 50%;
            width: 20px;
            height: 20px;
            display: flex;
            align-items: center;
            justify-content: center;
            box-shadow: 0 2px 4px rgba(0,0,0,0.2);
            pointer-events: none;
          ">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="#fff" stroke="#fff" stroke-width="2">
              <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon>
            </svg>
          </div>
        ` : ''}
      </div>
    `,
    iconSize: [56, 56],
    iconAnchor: [28, 56],
    popupAnchor: [0, -50]
  });
};

// Cluster icon
const createClusterIcon = (cluster) => {
  const count = cluster.getChildCount();
  const size = count < 10 ? 44 : count < 50 ? 52 : 60;
  
  return L.divIcon({
    html: `
      <div class="cluster-icon-inner" style="
        background: linear-gradient(135deg, #D4A574 0%, #C49A6C 100%);
        width: ${size}px;
        height: ${size}px;
        border-radius: 50%;
        border: 3px solid white;
        box-shadow: 0 4px 15px rgba(0,0,0,0.35);
        display: flex;
        align-items: center;
        justify-content: center;
        color: white;
        font-weight: bold;
        font-size: ${size < 50 ? '15px' : '17px'};
        cursor: pointer;
        transition: transform 0.2s ease, box-shadow 0.2s ease;
      ">
        <span>${count}</span>
      </div>
      <style>
        .cluster-icon-inner:hover {
          transform: scale(1.15);
          box-shadow: 0 6px 20px rgba(212, 165, 116, 0.5);
        }
      </style>
    `,
    className: 'custom-cluster-icon',
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2]
  });
};

// Hover Card Component with smart positioning
function HoverCard({ marker, position, containerBounds, onMouseEnter, onMouseLeave, onViewDetails }) {
  const { i18n, t } = useTranslation();
  const lang = i18n.language || 'es';
  
  if (!marker || !position) return null;
  
  const getLocalizedText = (obj) => {
    if (!obj) return '';
    if (typeof obj === 'string') return obj;
    return obj[lang] || obj.es || obj.en || '';
  };
  
  // Get the profile link identifier (username preferred over user_id)
  const getProfileIdentifier = () => {
    if (marker.type === 'service') {
      return marker.teacher_username || marker.teacher_user_id;
    } else {
      return marker.school_username || marker.school_user_id;
    }
  };
  
  const profileIdentifier = getProfileIdentifier();
  const hasProfileLink = !!profileIdentifier;
  
  // Card dimensions
  const cardWidth = 288; // w-72 = 18rem = 288px
  const cardHeight = 320; // increased height for new button
  const margin = 20; // minimum margin from edges
  
  // Use container bounds if available, otherwise use defaults
  const boundsWidth = containerBounds?.width || 900;
  const boundsHeight = containerBounds?.height || 550;
  
  // Calculate optimal position
  let finalX = position.x;
  let finalY = position.y;
  let transformX = '-50%';
  let transformY = '-100%'; // default: card above marker
  let arrowPosition = 'bottom';
  
  // Vertical positioning
  const spaceAbove = position.y;
  const spaceBelow = boundsHeight - position.y;
  
  if (spaceAbove >= cardHeight + margin) {
    // Enough space above - show card above marker
    transformY = '-100%';
    finalY = position.y - 60; // offset up from marker
    arrowPosition = 'bottom';
  } else if (spaceBelow >= cardHeight + margin + 60) {
    // Not enough space above but enough below - show card below marker
    transformY = '0';
    finalY = position.y + 60; // offset down from marker
    arrowPosition = 'top';
  } else {
    // Limited space both ways - show above and let it scroll if needed
    transformY = '-100%';
    finalY = Math.max(cardHeight + margin, position.y - 60);
    arrowPosition = 'bottom';
  }
  
  // Horizontal positioning
  const halfCard = cardWidth / 2;
  
  if (position.x < halfCard + margin) {
    // Too close to left edge - shift card right
    finalX = halfCard + margin;
  } else if (position.x > boundsWidth - halfCard - margin) {
    // Too close to right edge - shift card left  
    finalX = boundsWidth - halfCard - margin;
  }
  
  const handleShareProfile = (e) => {
    e.stopPropagation();
    if (profileIdentifier) {
      window.open(`/p/${profileIdentifier}`, '_blank');
    }
  };
  
  return (
    <div 
      className="absolute z-[1000] animate-fade-in pointer-events-auto"
      style={{ 
        left: finalX, 
        top: finalY,
        transform: `translate(${transformX}, ${transformY})`
      }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <div className="bg-white rounded-xl shadow-2xl overflow-hidden w-72 border border-gray-100">
        {/* Image */}
        <div className="relative h-36 bg-gradient-to-br from-amber-100 to-amber-50">
          {marker.image ? (
            <img src={marker.image} alt="" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              {marker.type === 'service' ? (
                <Heart className="w-12 h-12 text-amber-300" />
              ) : (
                <GraduationCap className="w-12 h-12 text-green-300" />
              )}
            </div>
          )}
          {/* Type Badge */}
          <div 
            className="absolute top-3 left-3 px-2 py-1 rounded-full text-xs font-medium text-white"
            style={{ backgroundColor: typeColors[marker.type] }}
          >
            {marker.type === 'service' ? 'Sesión' : 'Curso'}
          </div>
          {/* Google Rating Badge - prioritize over featured */}
          {marker.google_rating ? (
            <div className="absolute top-3 right-3 inline-flex items-center gap-1 px-2 py-1 bg-white/95 rounded-full shadow-sm">
              <Star className="w-3.5 h-3.5 text-yellow-400 fill-yellow-400" />
              <span className="text-sm font-semibold text-gray-800">{marker.google_rating.toFixed(1)}</span>
              {marker.google_reviews_count && (
                <span className="text-xs text-gray-500">({marker.google_reviews_count})</span>
              )}
            </div>
          ) : marker.featured && (
            <div className="absolute top-3 right-3 bg-yellow-400 rounded-full p-1.5">
              <Star className="w-4 h-4 text-white fill-white" />
            </div>
          )}
        </div>
        
        {/* Content */}
        <div className="p-4">
          <h3 className="font-semibold text-gray-900 text-lg leading-tight mb-1 line-clamp-1">
            {getLocalizedText(marker.title)}
          </h3>
          
          {/* Professional/School info with profile link */}
          {marker.type === 'service' && marker.teacher_name && (
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm text-gray-500">por {marker.teacher_name}</p>
              {hasProfileLink && (
                <button
                  onClick={handleShareProfile}
                  className="text-xs text-amber-600 hover:text-amber-700 flex items-center gap-1 hover:underline"
                  title={t('profile.viewProfile', 'Ver perfil')}
                >
                  <User className="w-3 h-3" />
                  Perfil
                </button>
              )}
            </div>
          )}
          
          {marker.type === 'course' && marker.school_name && (
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm text-gray-500">{marker.school_name}</p>
              {hasProfileLink && (
                <button
                  onClick={handleShareProfile}
                  className="text-xs text-green-600 hover:text-green-700 flex items-center gap-1 hover:underline"
                  title={t('profile.viewProfile', 'Ver perfil')}
                >
                  <User className="w-3 h-3" />
                  Perfil
                </button>
              )}
            </div>
          )}
          
          <div className="flex items-center justify-between">
            <div className="flex items-center text-gray-600">
              <MapPin className="w-4 h-4 mr-1" />
              <span className="text-sm">{marker.city}</span>
            </div>
            {marker.price > 0 && (
              <div className="flex items-center font-semibold text-gray-900">
                <Euro className="w-4 h-4 mr-0.5" />
                <span>{marker.price}</span>
              </div>
            )}
          </div>
          
          <Button 
            onClick={(e) => {
              e.stopPropagation();
              onViewDetails(marker);
            }}
            className="w-full mt-3 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white"
          >
            Ver Detalles <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </div>
      {/* Arrow - position dynamically based on card placement */}
      {arrowPosition === 'bottom' && (
        <div 
          className="absolute left-1/2 -translate-x-1/2 w-4 h-4 bg-white rotate-45 border-r border-b border-gray-100"
          style={{ bottom: -8 }}
        />
      )}
      {arrowPosition === 'top' && (
        <div 
          className="absolute left-1/2 -translate-x-1/2 w-4 h-4 bg-white rotate-45 border-l border-t border-gray-100"
          style={{ top: -8 }}
        />
      )}
    </div>
  );
}

export default function MapViewDesktop() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const mapRef = useRef(null);
  const containerRef = useRef(null);
  const mapContainerRef = useRef(null);
  const hoverTimeoutRef = useRef(null);
  const hasEnteredCardRef = useRef(false); // Track if mouse ever entered the card
  const clusterGroupRef = useRef(null);
  const currentSpiderfiedCluster = useRef(null); // Track currently spiderfied cluster
  
  const [markers, setMarkers] = useState([]);
  const [filteredMarkers, setFilteredMarkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');
  const [hoveredMarker, setHoveredMarker] = useState(null);
  const [hoverPosition, setHoverPosition] = useState(null);
  const [mapBounds, setMapBounds] = useState({ width: 0, height: 0 });

  const lang = i18n.language || 'es';
  
  const getLocalizedText = useCallback((obj) => {
    if (!obj) return '';
    if (typeof obj === 'string') return obj;
    return obj[lang] || obj.es || obj.en || '';
  }, [lang]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, []);

  // Calculate map container bounds
  useEffect(() => {
    const updateBounds = () => {
      if (mapContainerRef.current) {
        const rect = mapContainerRef.current.getBoundingClientRect();
        setMapBounds({ width: rect.width, height: rect.height });
      }
    };
    updateBounds();
    window.addEventListener('resize', updateBounds);
    return () => window.removeEventListener('resize', updateBounds);
  }, []);

  // Load data
  useEffect(() => {
    const loadMapData = async () => {
      try {
        const [servicesRes, coursesRes] = await Promise.all([
          axios.get(`${API_URL}/api/services`),
          axios.get(`${API_URL}/api/courses`)
        ]);

        const serviceMarkers = servicesRes.data
          .filter(s => s.location?.latitude && s.location?.longitude)
          .map(s => ({
            id: s.id,
            type: 'service',
            title: s.title,
            latitude: s.location.latitude,
            longitude: s.location.longitude,
            city: s.location.city,
            province: s.location.province || '',
            area: s.location.area || '',
            image: s.images?.[0] || null,
            price: s.price || 0,
            featured: s.featured || false,
            teacher_name: s.teacher_name,
            teacher_user_id: s.teacher_user_id || null,
            teacher_username: s.teacher_username || null,
            google_rating: s.google_rating || null,
            google_reviews_count: s.google_reviews_count || null
          }));

        const courseMarkers = coursesRes.data
          .filter(c => c.location?.latitude && c.location?.longitude)
          .map(c => ({
            id: c.id,
            type: 'course',
            title: c.title,
            latitude: c.location.latitude,
            longitude: c.location.longitude,
            city: c.location.city,
            province: c.location.province || '',
            area: c.location.area || '',
            image: c.images?.[0] || null,
            price: c.price || 0,
            featured: c.featured || false,
            school_name: c.school_name,
            school_user_id: c.school_user_id || null,
            school_username: c.school_username || null,
            start_date: c.start_date,
            google_rating: c.google_rating || null,
            google_reviews_count: c.google_reviews_count || null
          }));

        const allMarkers = [...serviceMarkers, ...courseMarkers];
        
        // Apply small offset to markers at same coordinates so they're visible when zoomed
        const offsetMarkers = applyCoordinateOffset(allMarkers);
        
        setMarkers(offsetMarkers);
        setFilteredMarkers(offsetMarkers);
      } catch (error) {
        console.error('Error loading map data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadMapData();
  }, []);

  // Filter markers
  useEffect(() => {
    let filtered = markers;

    if (activeFilter !== 'all') {
      filtered = filtered.filter(m => m.type === activeFilter);
    }

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(m => 
        getLocalizedText(m.title).toLowerCase().includes(query) ||
        m.city?.toLowerCase().includes(query) ||
        m.province?.toLowerCase().includes(query) ||
        m.area?.toLowerCase().includes(query)
      );
    }

    setFilteredMarkers(filtered);
  }, [markers, activeFilter, searchQuery]);

  // Restore state when coming back from detail page
  useEffect(() => {
    const savedMarkerId = sessionStorage.getItem('mapDesktopSelectedMarkerId');
    const savedMarkerType = sessionStorage.getItem('mapDesktopSelectedMarkerType');
    const savedActiveFilter = sessionStorage.getItem('mapDesktopActiveFilter');
    const savedSearchQuery = sessionStorage.getItem('mapDesktopSearchQuery');
    
    if (savedMarkerId && markers.length > 0) {
      // Restore filter and search state first
      if (savedActiveFilter) {
        setActiveFilter(savedActiveFilter);
      }
      if (savedSearchQuery) {
        setSearchQuery(savedSearchQuery);
      }
      
      // Find the saved marker and center the map on it
      const savedMarker = markers.find(m => m.id === savedMarkerId && m.type === savedMarkerType);
      if (savedMarker && mapRef.current) {
        // Center map on the marker after a brief delay to let the map initialize
        setTimeout(() => {
          if (mapRef.current) {
            const map = mapRef.current;
            map.setView([savedMarker.latitude, savedMarker.longitude], 12, { animate: true });
          }
        }, 300);
      }
      
      // Clear saved state
      sessionStorage.removeItem('mapDesktopSelectedMarkerId');
      sessionStorage.removeItem('mapDesktopSelectedMarkerType');
      sessionStorage.removeItem('mapDesktopActiveFilter');
      sessionStorage.removeItem('mapDesktopSearchQuery');
    }
  }, [markers]);

  const handleViewDetails = (marker) => {
    if (!marker || !marker.id || !marker.type) return;
    
    // Save state before navigating for scroll restoration
    sessionStorage.setItem('mapDesktopSelectedMarkerId', marker.id);
    sessionStorage.setItem('mapDesktopSelectedMarkerType', marker.type);
    sessionStorage.setItem('mapDesktopActiveFilter', activeFilter);
    sessionStorage.setItem('mapDesktopSearchQuery', searchQuery);
    
    // Close hover card before navigating
    hasEnteredCardRef.current = false;
    setHoveredMarker(null);
    setHoverPosition(null);
    
    const routes = {
      service: `/service/${marker.id}`,
      course: `/course/${marker.id}`
    };
    navigate(routes[marker.type]);
  };

  const handleMarkerMouseOver = (marker, e) => {
    // Clear any pending close timeout
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    
    // If there's already a card open for a different marker, close it first
    if (hoveredMarker && hoveredMarker.id !== marker.id) {
      // Close the current card first
      hasEnteredCardRef.current = false;
      setHoveredMarker(null);
      setHoverPosition(null);
      
      // Then open the new card after a brief delay to let React update
      setTimeout(() => {
        if (mapContainerRef.current) {
          const rect = mapContainerRef.current.getBoundingClientRect();
          const x = e.originalEvent.clientX - rect.left;
          const y = e.originalEvent.clientY - rect.top;
          
          setMapBounds({ width: rect.width, height: rect.height });
          setHoverPosition({ x, y });
          setHoveredMarker(marker);
          // Don't auto-set hasEnteredCardRef - wait for actual mouseenter
        }
      }, 50);
      return;
    }
    
    // No card open, just show the new one
    if (mapContainerRef.current) {
      const rect = mapContainerRef.current.getBoundingClientRect();
      const x = e.originalEvent.clientX - rect.left;
      const y = e.originalEvent.clientY - rect.top;
      
      // Reset hasEnteredCard when showing a new card
      hasEnteredCardRef.current = false;
      
      // Always update bounds with current values
      setMapBounds({ width: rect.width, height: rect.height });
      setHoverPosition({ x, y });
      setHoveredMarker(marker);
      // Don't auto-set hasEnteredCardRef - wait for actual mouseenter
    }
  };

  const handleMarkerMouseOut = () => {
    // Don't close immediately - the card stays open until:
    // 1. User enters the card and then leaves it
    // 2. User clicks on the map
    // 3. User hovers over another marker
    // So we don't auto-close on marker mouseout anymore
  };

  const handleCardMouseEnter = () => {
    // Small delay to avoid false triggers during card repositioning
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    hoverTimeoutRef.current = setTimeout(() => {
      hasEnteredCardRef.current = true;
    }, 150); // 150ms delay to ensure card is stable
  };

  const handleCardMouseLeave = () => {
    // Clear any pending enter timeout
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    
    // Only close if the user had actually entered the card
    if (hasEnteredCardRef.current) {
      hasEnteredCardRef.current = false;
      setHoveredMarker(null);
      setHoverPosition(null);
    }
  };

  const handleMapClick = (e) => {
    // Close the hover card when clicking on the map (outside the card)
    if (hoveredMarker) {
      hasEnteredCardRef.current = false;
      setHoveredMarker(null);
      setHoverPosition(null);
    }
  };

  // Close hover card when hovering on a different single marker (not in spiderfy)
  const handleSingleMarkerHover = (marker, e) => {
    // If there's a spiderfied cluster, unspiderfy it
    if (currentSpiderfiedCluster.current && clusterGroupRef.current) {
      try {
        currentSpiderfiedCluster.current.unspiderfy();
        currentSpiderfiedCluster.current = null;
      } catch (err) {
        // Ignore errors
      }
    }
    handleMarkerMouseOver(marker, e);
  };

  if (loading) {
    return (
      <div className="h-[600px] flex items-center justify-center bg-gradient-to-br from-amber-50 to-white rounded-2xl">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-amber-200 border-t-amber-500 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-500">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  // Get translated labels
  const allLabel = t('common.all');
  const sessionsLabel = t('common.sessions');
  const coursesLabel = t('course.title');
  const searchPlaceholder = t('common.searchPlaceholder');

  return (
    <div className="relative" ref={containerRef}>
      {/* Header with Search and Filters */}
      <div className="bg-white rounded-t-2xl border-b border-gray-100 p-4">
        <div className="flex flex-wrap items-center gap-4">
          {/* Search */}
          <div className="relative flex-1 min-w-[250px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <Input
              placeholder={searchPlaceholder}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 pr-10 h-11 bg-gray-50 border-gray-200 focus:bg-white"
            />
            {searchQuery && (
              <button 
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          
          {/* Filter Buttons */}
          <div className="flex gap-2">
            <Button
              variant={activeFilter === 'all' ? 'default' : 'outline'}
              onClick={() => setActiveFilter('all')}
              className={activeFilter === 'all' ? 'bg-gray-900 text-white' : ''}
            >
              {allLabel} ({markers.length})
            </Button>
            <Button
              variant={activeFilter === 'service' ? 'default' : 'outline'}
              onClick={() => setActiveFilter('service')}
              className={activeFilter === 'service' ? 'text-white' : 'border-amber-300 text-amber-700 hover:bg-amber-50'}
              style={activeFilter === 'service' ? { backgroundColor: typeColors.service } : {}}
            >
              <Heart className="w-4 h-4 mr-1" />
              {sessionsLabel} ({markers.filter(m => m.type === 'service').length})
            </Button>
            <Button
              variant={activeFilter === 'course' ? 'default' : 'outline'}
              onClick={() => setActiveFilter('course')}
              className={activeFilter === 'course' ? 'text-white' : 'border-green-300 text-green-700 hover:bg-green-50'}
              style={activeFilter === 'course' ? { backgroundColor: typeColors.course } : {}}
            >
              <GraduationCap className="w-4 h-4 mr-1" />
              {coursesLabel} ({markers.filter(m => m.type === 'course').length})
            </Button>
          </div>
        </div>
      </div>

      {/* Map Container */}
      <div ref={mapContainerRef} className="relative h-[550px] rounded-b-2xl overflow-hidden">
        <MapContainer
          ref={mapRef}
          center={[28.1, -16.6]}
          zoom={9}
          style={{ height: '100%', width: '100%' }}
          scrollWheelZoom={true}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          
          <FitBounds markers={filteredMarkers} />
          <MapClickHandler onMapClick={handleMapClick} />
          
          <MarkerClusterGroup
            key={`cluster-${filteredMarkers.length}-${activeFilter}`}
            ref={clusterGroupRef}
            chunkedLoading
            removeOutsideVisibleBounds={false}
            iconCreateFunction={createClusterIcon}
            maxClusterRadius={50}
            spiderfyOnMaxZoom={true}
            zoomToBoundsOnClick={true}
            showCoverageOnHover={false}
            disableClusteringAtZoom={15}
            spiderfyDistanceMultiplier={4}
            spiderLegPolylineOptions={{ weight: 2, color: '#D4A574', opacity: 0.7 }}
            eventHandlers={{
              clusterclick: (e) => {
                // Close any hover card FIRST
                hasEnteredCardRef.current = false;
                setHoveredMarker(null);
                setHoverPosition(null);
              }
            }}
          >
            {filteredMarkers
              .filter(marker => 
                marker && 
                typeof marker.latitude === 'number' && 
                typeof marker.longitude === 'number' &&
                !isNaN(marker.latitude) && 
                !isNaN(marker.longitude)
              )
              .map((marker) => (
              <Marker
                key={`${marker.type}-${marker.id}`}
                position={[marker.latitude, marker.longitude]}
                icon={createPhotoMarker(marker, marker.featured)}
                eventHandlers={{
                  mouseover: (e) => handleMarkerMouseOver(marker, e),
                  mouseout: handleMarkerMouseOut,
                  click: (e) => {
                    // Close hover card first, then navigate
                    e.originalEvent?.stopPropagation();
                    hasEnteredCardRef.current = false;
                    setHoveredMarker(null);
                    setHoverPosition(null);
                    // Small delay to let state settle
                    setTimeout(() => handleViewDetails(marker), 50);
                  }
                }}
              />
            ))}
          </MarkerClusterGroup>
        </MapContainer>

        {/* Hover Card */}
        {hoveredMarker && hoverPosition && (
          <HoverCard
            marker={hoveredMarker}
            position={hoverPosition}
            containerBounds={mapBounds}
            onMouseEnter={handleCardMouseEnter}
            onMouseLeave={handleCardMouseLeave}
            onViewDetails={handleViewDetails}
          />
        )}

        {/* Results Count */}
        <div className="absolute bottom-4 left-4 bg-white/95 backdrop-blur-sm rounded-lg px-4 py-2 shadow-lg">
          <p className="text-sm font-medium text-gray-700">
            {filteredMarkers.length} resultado{filteredMarkers.length !== 1 ? 's' : ''}
            {searchQuery && ` para "${searchQuery}"`}
          </p>
        </div>
      </div>

      {/* CSS for animations */}
      <style>{`
        .marker-pulse .marker-container > div:last-child {
          animation: pulse 2s infinite;
        }
        
        @keyframes pulse {
          0%, 100% { box-shadow: 0 4px 12px rgba(0,0,0,0.25); }
          50% { box-shadow: 0 4px 20px rgba(212, 165, 116, 0.6); }
        }
        
        .custom-photo-marker:hover .marker-container {
          transform: scale(1.1);
        }
        
        .animate-fade-in {
          animation: fadeIn 0.2s ease-out;
        }
        
        @keyframes fadeIn {
          from { opacity: 0; transform: translate(-50%, -100%) translateY(10px); }
          to { opacity: 1; transform: translate(-50%, -100%) translateY(0); }
        }
        
        .line-clamp-1 {
          display: -webkit-box;
          -webkit-line-clamp: 1;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
        
        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
    </div>
  );
}
